import React from 'react';
import PropTypes from 'prop-types';
import Theme from "./Theme.js";
import S from './Toggle.css';

/**
 * React class that renders a Toggle
 * The toggle is a checkbox button that looks like a switch
 * This is based off of:
 * https://github.com/markusenglund/react-switch/blob/master/src/index.jsx
 * Modifications include using css for attributes, getting the size of handle
 * from the dom itself, and changing the event given to onChange so its consistent
 * with the other widgets 
 * @prop {function} onChange - function to be called when the toggle is clicked or dragged 
 * @prop {any} eventData - will be passed through as the data key to the onChange function 
 * @prop {boolean} value - whether the checkbox is on or off 
 * @prop {string} className - css class to be applied to this component 
 * @prop {string} variant - Theme variant name to be added to the component 
 * @prop {boolean} disabled - if true, user will not be able to interact with the component 
 * @prop {string} onIcon - font string representing the icon that will be displyed when value is true 
 * @prop {string} offIcon - font string representing the icon that will be displyed when value is false 
 * @prop {node} children - internal component children, will be handled by react 
 **/
/* eslint-disable react/no-unused-state */
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
class Toggle extends React.Component {
  static contextType = Theme.Context;
  static propTypes = {
    onChange: PropTypes.func,
    eventData: PropTypes.any,
    value: PropTypes.bool,
    className: PropTypes.string,
    variant: PropTypes.string,
    disabled: PropTypes.bool,
    onIcon: PropTypes.string,
    offIcon: PropTypes.string,
    children: PropTypes.node,
    stopPropagation: PropTypes.bool
  };
  static defaultProps = {
    onChange: null,
    eventData: null,
    value: null,
    className: null,
    variant: null,
    disabled: null,
    onIcon: null,
    offIcon: null,
    children: null,
    stopPropagation: true
  };
  constructor(props) {
    super(props);
    this.handleRef = /*#__PURE__*/React.createRef();
    this.backgroundRef = /*#__PURE__*/React.createRef();
    this.containerRef = /*#__PURE__*/React.createRef();
    this.state = {
      startX: null,
      isDragging: false,
      hasOutline: false
    };
  }

  /**
   * (Internal) calls prop onChange with an object with a value property, data property and an event key
   * the value property is the inverse of the current value, and the data is the eventData prop, and the event key is the click event
   */
  onChange = (value, event) => {
    const {
      onChange,
      eventData
    } = this.props;
    if (typeof onChange === 'function') {
      onChange({
        value,
        data: eventData,
        event
      });
    }
  };
  componentWillReceiveProps({
    value
  }) {
    const pos = value ? this.checkedPos : this.uncheckedPos;
    this.setState({
      pos
    });
  }

  /**
   * (Internal) method to handle the toggle drag functionality
   */
  onDragStart = clientX => {
    const hw = this.handleRef.current.offsetWidth;
    const bw = this.backgroundRef.current.offsetWidth;
    this.uncheckedPos = 0;
    this.checkedPos = bw - hw;
    this.setState({
      startX: clientX,
      hasOutline: true
    });
  };

  /**
   * (Internal) method to handle the toggle drag functionality
   */
  onDrag = clientX => {
    const {
      startX
    } = this.state;
    const {
      value
    } = this.props;
    const startPos = value ? this.checkedPos : this.uncheckedPos;
    const newPos = startPos + clientX - startX;
    const pos = Math.min(this.checkedPos, Math.max(this.uncheckedPos, newPos));
    this.setState({
      pos,
      isDragging: true
    });
  };

  /**
   * (Internal) method to handle the toggle drag functionality
   */
  onDragStop = event => {
    const {
      pos,
      isDragging
    } = this.state;
    const {
      value
    } = this.props;

    // Simulate clicking the handle
    if (!isDragging) {
      this.setState({
        startX: null,
        hasOutline: false
      });
      this.onChange(!value, event);
      return;
    }
    if (value) {
      if (pos > (this.checkedPos + this.uncheckedPos) / 2) {
        this.setState({
          pos: this.checkedPos,
          startX: null,
          isDragging: false,
          hasOutline: false
        });
        return;
      }
      this.setState({
        startX: null,
        isDragging: false,
        hasOutline: false
      });
      this.onChange(false, event);
      return;
    }
    if (pos < (this.checkedPos + this.uncheckedPos) / 2) {
      this.setState({
        pos: this.uncheckedPos,
        startX: null,
        isDragging: false,
        hasOutline: false
      });
      return;
    }
    this.setState({
      startX: null,
      isDragging: false,
      hasOutline: false
    });
    this.onChange(true, event);
  };

  /** 
   * (Internal) event to handle mouseclicks
   * */
  onMouseDown = event => {
    // Ignore right click and scroll
    if (typeof event.button === 'number' && event.button !== 0) {
      return;
    }
    this.onDragStart(event.clientX);
    document.addEventListener('mousemove', this.onMouseMove);
    document.addEventListener('mouseup', this.onMouseUp);
  };

  /** 
   * (Internal) event to handle mouseclicks
   * */
  onMouseMove = event => {
    event.preventDefault();
    this.onDrag(event.clientX);
  };

  /** 
   * (Internal) event to handle mouseclicks
   * */
  onMouseUp = event => {
    this.onDragStop(event);
    document.removeEventListener('mousemove', this.onMouseMove);
    document.removeEventListener('mouseup', this.onMouseUp);
  };

  /** 
   * (Internal) event to handle touch events
   * */
  onTouchStart = event => {
    this.onDragStart(event.touches[0].clientX);
  };

  /** 
   * (Internal) event to handle touch events
   * */
  onTouchMove = event => {
    this.onDrag(event.touches[0].clientX);
  };

  /** 
   * (Internal) event to handle touch events
   * */
  onTouchEnd = event => {
    event.preventDefault();
    this.onDragStop(event);
  };

  /** 
   * (Internal) event to handle touch events
   * */
  onTouchCancel = () => {
    this.setState({
      startX: null,
      hasOutline: false
    });
  };

  /** 
   * (Internal) click handler for the element
   */
  onClick = event => {
    const {
      value,
      stopPropagation
    } = this.props;
    if (stopPropagation) {
      event.stopPropagation();
    }
    this.onChange(!value, event);
    this.containerRef.current?.focus();
  };

  /**
   * (Internal) triggers if enter key is pressed
   */
  onKeyDown = event => {
    const {
      value
    } = this.props;
    const {
      isDragging
    } = this.state;
    // Trigger change on spacebar and enter keys (in violation of wai-aria spec).
    if ((event.keyCode === 32 || event.keyCode === 13) && !isDragging) {
      event.preventDefault();
      this.onChange(!value, event);
    }
  };
  render() {
    const theme = this.context;
    /* eslint-disable */
    const rootStyle = null,
      onStyle = null,
      offStyle = null,
      backgroundStyle = null;
    const {
      className,
      variant,
      disabled = false,
      value = false,
      onIcon,
      offIcon,
      children,
      onChange,
      eventData,
      layoutOptions,
      stopPropagation,
      ...rest
    } = this.props;
    /* eslint-enable */
    const {
      isDragging,
      pos
    } = this.state;
    const handleStyle = isDragging ? {
      transform: `translateX(${pos}px)`
    } : null;
    const toggleCn = theme.classes('Toggle', 'toggle', variant, className, S.toggle);
    const onCn = theme.subclasses('Toggle', 'onIcon');
    const offCn = theme.subclasses('Toggle', 'offIcon');
    const backgroundCn = theme.subclasses('Toggle', 'background', S.background);
    const handleCn = theme.subclasses('Toggle', 'handle', S.handle);
    return /*#__PURE__*/_jsxs("div", {
      className: toggleCn,
      style: rootStyle,
      "data-value": value,
      "data-disabled": disabled,
      role: "checkbox",
      "aria-checked": value,
      tabIndex: disabled ? null : 0,
      ref: this.containerRef,
      ...rest,
      children: [/*#__PURE__*/_jsxs("div", {
        className: backgroundCn,
        ref: this.backgroundRef,
        onClick: disabled ? null : this.onClick,
        style: backgroundStyle,
        children: [onIcon && /*#__PURE__*/_jsx("div", {
          className: onCn,
          style: onStyle,
          children: onIcon
        }), offIcon && /*#__PURE__*/_jsx("div", {
          className: offCn,
          style: offStyle,
          children: offIcon
        })]
      }), /*#__PURE__*/_jsx("div", {
        className: handleCn,
        ref: this.handleRef,
        onMouseDown: disabled ? null : this.onMouseDown,
        onTouchStart: disabled ? null : this.onTouchStart,
        onTouchMove: disabled ? null : this.onTouchMove,
        onTouchEnd: disabled ? null : this.onTouchEnd,
        onTouchCancel: disabled ? null : this.onTouchCancel,
        onKeyDown: this.onKeyDown,
        style: handleStyle
      })]
    });
  }
}
/* eslint-enable react/no-unused-state */

export default Toggle;